import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export interface DropboxFile {
  id: string;
  folder_id: string | null;
  owner_id: number;
  name: string;
  extension: string | null;
  mime_type: string | null;
  size_bytes: number;
  checksum: string | null;
  is_global: boolean;
  is_starred: boolean;
  is_deleted: boolean;
  tags: string[];
  description: string | null;
  sort_order: number;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
}

export interface DropboxFolder {
  id: string;
  parent_id: string | null;
  owner_id: number;
  name: string;
  path: string;
  depth: number;
  is_global: boolean;
  is_deleted: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
}

export interface StorageUsage {
  used_bytes: number;
  quota_bytes: number;
  percentage: number;
}

export interface RecentActivity {
  id: string;
  user_id: number;
  entity_type: 'file' | 'folder';
  entity_id: string;
  action: string;
  accessed_at: string;
  entity?: {
    id: string;
    name: string;
    type: 'file' | 'folder';
    folder_id?: string;
    is_deleted: boolean;
  };
}

export interface DropboxState {
  currentFolderId: string | null;
  folders: DropboxFolder[];
  files: DropboxFile[];
  selectedItems: string[];
  viewMode: 'grid' | 'list';
  sortBy: 'name' | 'date' | 'size' | 'custom';
  sortDirection: 'asc' | 'desc';
  searchQuery: string;
  selectedTags: string[];
  allTags: string[];
  recentActivity: RecentActivity[];
  storageUsage: StorageUsage | null;
  loading: boolean;
  error: string | null;
  uploadProgress: { [key: string]: number };
}

const initialState: DropboxState = {
  currentFolderId: null,
  folders: [],
  files: [],
  selectedItems: [],
  viewMode: 'grid',
  sortBy: 'name',
  sortDirection: 'asc',
  searchQuery: '',
  selectedTags: [],
  allTags: [],
  recentActivity: [],
  storageUsage: null,
  loading: false,
  error: null,
  uploadProgress: {},
};

export const dropboxSlice = createSlice({
  name: 'dropbox',
  initialState,
  reducers: {
    // Folder actions
    setCurrentFolder: (state, action: PayloadAction<string | null>) => {
      state.currentFolderId = action.payload;
      state.selectedItems = [];
    },
    setFolders: (state, action: PayloadAction<DropboxFolder[]>) => {
      state.folders = action.payload;
    },
    addFolder: (state, action: PayloadAction<DropboxFolder>) => {
      state.folders.push(action.payload);
    },
    updateFolder: (state, action: PayloadAction<DropboxFolder>) => {
      const index = state.folders.findIndex((f) => f.id === action.payload.id);
      if (index !== -1) {
        state.folders[index] = action.payload;
      }
    },
    removeFolder: (state, action: PayloadAction<string>) => {
      state.folders = state.folders.filter((f) => f.id !== action.payload);
    },

    // File actions
    setFiles: (state, action: PayloadAction<DropboxFile[]>) => {
      state.files = action.payload;
    },
    addFile: (state, action: PayloadAction<DropboxFile>) => {
      state.files.push(action.payload);
    },
    updateFile: (state, action: PayloadAction<DropboxFile>) => {
      const index = state.files.findIndex((f) => f.id === action.payload.id);
      if (index !== -1) {
        state.files[index] = action.payload;
      }
    },
    removeFile: (state, action: PayloadAction<string>) => {
      state.files = state.files.filter((f) => f.id !== action.payload);
    },

    // Selection actions
    selectItem: (state, action: PayloadAction<string>) => {
      if (!state.selectedItems.includes(action.payload)) {
        state.selectedItems.push(action.payload);
      }
    },
    deselectItem: (state, action: PayloadAction<string>) => {
      state.selectedItems = state.selectedItems.filter((id) => id !== action.payload);
    },
    selectAll: (state) => {
      state.selectedItems = [...state.files.map((f) => f.id), ...state.folders.map((f) => f.id)];
    },
    clearSelection: (state) => {
      state.selectedItems = [];
    },

    // View actions
    setViewMode: (state, action: PayloadAction<'grid' | 'list'>) => {
      state.viewMode = action.payload;
    },
    setSortBy: (state, action: PayloadAction<'name' | 'date' | 'size' | 'custom'>) => {
      state.sortBy = action.payload;
    },
    setSortDirection: (state, action: PayloadAction<'asc' | 'desc'>) => {
      state.sortDirection = action.payload;
    },

    // Search and filter actions
    setSearchQuery: (state, action: PayloadAction<string>) => {
      state.searchQuery = action.payload;
    },
    setSelectedTags: (state, action: PayloadAction<string[]>) => {
      state.selectedTags = action.payload;
    },
    addSelectedTag: (state, action: PayloadAction<string>) => {
      if (!state.selectedTags.includes(action.payload)) {
        state.selectedTags.push(action.payload);
      }
    },
    removeSelectedTag: (state, action: PayloadAction<string>) => {
      state.selectedTags = state.selectedTags.filter((tag) => tag !== action.payload);
    },
    clearSelectedTags: (state) => {
      state.selectedTags = [];
    },

    // Tags management
    setAllTags: (state, action: PayloadAction<string[]>) => {
      state.allTags = action.payload;
    },

    // Recent activity
    setRecentActivity: (state, action: PayloadAction<RecentActivity[]>) => {
      state.recentActivity = action.payload;
    },

    // Storage
    setStorageUsage: (state, action: PayloadAction<StorageUsage>) => {
      state.storageUsage = action.payload;
    },

    // Upload progress
    setUploadProgress: (state, action: PayloadAction<{ fileId: string; progress: number }>) => {
      state.uploadProgress[action.payload.fileId] = action.payload.progress;
    },
    removeUploadProgress: (state, action: PayloadAction<string>) => {
      delete state.uploadProgress[action.payload];
    },
    clearUploadProgress: (state) => {
      state.uploadProgress = {};
    },

    // Loading and error
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },

    // Reset state
    resetDropbox: (state) => {
      return initialState;
    },
  },
});

export const {
  setCurrentFolder,
  setFolders,
  addFolder,
  updateFolder,
  removeFolder,
  setFiles,
  addFile,
  updateFile,
  removeFile,
  selectItem,
  deselectItem,
  selectAll,
  clearSelection,
  setViewMode,
  setSortBy,
  setSortDirection,
  setSearchQuery,
  setSelectedTags,
  addSelectedTag,
  removeSelectedTag,
  clearSelectedTags,
  setAllTags,
  setRecentActivity,
  setStorageUsage,
  setUploadProgress,
  removeUploadProgress,
  clearUploadProgress,
  setLoading,
  setError,
  resetDropbox,
} = dropboxSlice.actions;

export default dropboxSlice.reducer;
